import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CaretLeft, CaretRight, SunHorizon, MoonStars, Barbell, CloudMoon, Clock, CheckCircle, CalendarBlank, ListChecks, MapPin, Lightning } from "@phosphor-icons/react";

const todayIso = () => new Date().toISOString().slice(0, 10);
const shiftDate = (iso, delta) => {
  const d = new Date(iso + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
};
const fmtDate = (iso) =>
  new Date(iso + "T12:00:00Z").toLocaleDateString("ru-RU", { weekday: "short", day: "numeric", month: "long" });

const EVENT_STYLE = {
  wake: { icon: SunHorizon, color: "#ffea00" },
  nap: { icon: CloudMoon, color: "#a970ff" },
  workout: { icon: Barbell, color: "#0084ff" },
  bedtime: { icon: MoonStars, color: "#a970ff" },
  task: { icon: ListChecks, color: "#00e676" },
  event: { icon: CalendarBlank, color: "#4dabf5" },
  gps: { icon: MapPin, color: "#ff9100" },
};

const PRIORITY_RU = { high: "высокий", medium: "средний", low: "низкий" };

const hm = (h) => {
  if (h == null) return "—";
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${hh} ч ${mm} мин`;
};

function EventDetails({ ev }) {
  const d = ev.details || {};
  if (ev.type === "wake")
    return (
      <div className="text-xs text-muted-foreground space-y-0.5">
        <div>Сон: <span className="text-foreground num">{hm(d.sleep_h)}</span>{d.bed_time && <> · отбой накануне в <span className="text-foreground num">{d.bed_time}</span></>}</div>
        <div className="flex flex-wrap gap-x-3">
          {d.performance != null && <span>Качество <span className="text-foreground num">{d.performance}%</span></span>}
          {d.efficiency != null && <span>Эффективность <span className="text-foreground num">{d.efficiency}%</span></span>}
          {d.disturbances != null && <span>Пробуждений <span className="text-foreground num">{d.disturbances}</span></span>}
        </div>
        <div className="flex flex-wrap gap-x-3">
          <span>Глубокий <span className="text-foreground num">{hm(d.deep_h)}</span></span>
          <span>REM <span className="text-foreground num">{hm(d.rem_h)}</span></span>
          <span>Лёгкий <span className="text-foreground num">{hm(d.light_h)}</span></span>
        </div>
      </div>
    );
  if (ev.type === "workout")
    return (
      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
        {d.duration_min != null && <span><span className="text-foreground num">{d.duration_min}</span> мин</span>}
        {d.strain != null && <span>Нагрузка <span className="text-foreground num">{d.strain}</span></span>}
        {d.kcal != null && <span><span className="text-foreground num">{d.kcal}</span> ккал</span>}
        {d.avg_hr != null && <span>Пульс <span className="text-foreground num">{d.avg_hr}</span>{d.max_hr != null && <> / макс <span className="text-foreground num">{d.max_hr}</span></>}</span>}
        {d.distance_km != null && <span><span className="text-foreground num">{d.distance_km}</span> км</span>}
        {d.exercises && <span className="text-[#00c9a7]">Упражнения <span className="num">{d.exercises.filter((x) => x.done).length}/{d.exercises.length}</span></span>}
        {d.source && <span>{d.source}</span>}
      </div>
    );
  if (ev.type === "nap")
    return (
      <div className="text-xs text-muted-foreground">
        {d.duration_min != null && <span><span className="text-foreground num">{d.duration_min}</span> мин</span>}
        {d.performance != null && <span> · качество <span className="text-foreground num">{d.performance}%</span></span>}
      </div>
    );
  if (ev.type === "task")
    return <div className="text-xs text-muted-foreground">Задача выполнена{d.priority && d.priority !== "medium" ? ` · приоритет ${PRIORITY_RU[d.priority] || d.priority}` : ""}</div>;
  if (ev.type === "event")
    return <div className="text-xs text-muted-foreground">Событие календаря{d.source ? ` · ${d.source}` : ""}</div>;
  if (ev.type === "gps")
    return (
      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
        {d.distance_km != null && <span><span className="text-foreground num">{d.distance_km}</span> км</span>}
        {d.duration_min != null && <span><span className="text-foreground num">{d.duration_min}</span> мин</span>}
        {d.avg_speed_kmh != null && <span>ср. <span className="text-foreground num">{d.avg_speed_kmh}</span> км/ч</span>}
        {d.max_speed_kmh != null && <span>макс <span className="text-foreground num">{d.max_speed_kmh}</span> км/ч</span>}
      </div>
    );
  return null;
}

export default function DayStoryCard({ member = "" }) {
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);

  const toggleEx = (i) => {
    if (!sel?.details?.log_id || member) return;
    const upd = sel.details.exercises.map((it, idx) => (idx === i ? { ...it, done: !it.done } : it));
    setSel((s) => ({ ...s, details: { ...s.details, exercises: upd } }));
    setData((d) => ({ ...d, events: d.events.map((e) => (e.details?.log_id === sel.details.log_id ? { ...e, details: { ...e.details, exercises: upd } } : e)) }));
    api.post(`/workouts/${sel.details.log_id}/toggle/${i}`).catch(() => {});
  };

  useEffect(() => {
    setLoading(true);
    api.get(`/whoop/day-story?date=${date}${member ? `&member=${member}` : ""}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [date]);

  const s = data?.summary || {};
  const chips = [
    ["Восстановление", s.recovery != null ? `${s.recovery}%` : null, s.recovery == null ? "#555" : s.recovery >= 67 ? "#00e676" : s.recovery >= 34 ? "#ffea00" : "#ff3b30"],
    ["Нагрузка", s.strain, "#0084ff"],
    ["Сон", s.sleep_h != null ? hm(s.sleep_h) : null, "#a970ff"],
    ["Калории", s.kcal != null ? `${s.kcal}` : null, "#ffea00"],
    ["День бодрствования", s.awake_day_h != null ? `${s.awake_day_h} ч` : null, "#4dabf5"],
  ].filter(([, v]) => v != null);

  return (
    <div className="glass-card p-5" data-testid="day-story-card">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Clock size={16} className="text-primary" /> История дня
        </h3>
        <div className="flex items-center gap-1">
          <button onClick={() => setDate((d) => shiftDate(d, -1))} data-testid="day-story-prev"
            className="p-1.5 rounded-md bg-secondary text-muted-foreground hover:text-foreground transition-colors">
            <CaretLeft size={14} />
          </button>
          <span className="text-xs font-semibold min-w-[130px] text-center capitalize" data-testid="day-story-date">{fmtDate(date)}</span>
          <button onClick={() => setDate((d) => shiftDate(d, 1))} disabled={date >= todayIso()} data-testid="day-story-next"
            className="p-1.5 rounded-md bg-secondary text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30">
            <CaretRight size={14} />
          </button>
          {date !== todayIso() && (
            <button onClick={() => setDate(todayIso())} data-testid="day-story-today"
              className="ml-1 px-2 py-1 rounded-md bg-secondary text-xs text-muted-foreground hover:text-foreground transition-colors">
              Сегодня
            </button>
          )}
        </div>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="day-story-summary">
          {chips.map(([label, value, color]) => (
            <span key={label} className="px-2.5 py-1 rounded-md bg-secondary text-xs">
              <span className="text-muted-foreground">{label}: </span>
              <span className="font-semibold num" style={{ color }}>{value}</span>
            </span>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground py-6 text-center">Загружаем историю дня…</div>
      ) : !data || (data.events.length === 0 && !(data.habits_done?.length || data.allday?.length)) ? (
        <div className="text-sm text-muted-foreground py-6 text-center" data-testid="day-story-empty">Нет данных за этот день</div>
      ) : (
        <>
        {data.allday?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3" data-testid="day-story-allday">
            {data.allday.map((e, i) => (
              <span key={i} className="px-2.5 py-1 rounded-md bg-secondary text-xs flex items-center gap-1.5">
                <CalendarBlank size={12} className="text-[#4dabf5]" /> {e.title}
              </span>
            ))}
          </div>
        )}
        <div className="relative pl-1" data-testid="day-story-timeline">
          {data.events.map((ev, i) => {
            const { icon: Icon, color } = EVENT_STYLE[ev.type] || EVENT_STYLE.workout;
            const last = i === data.events.length - 1;
            return (
              <div key={i} className="flex gap-3" data-testid={`day-story-event-${i}`}>
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${color}1a`, border: `1px solid ${color}40` }}>
                    <Icon size={16} weight="duotone" style={{ color }} />
                  </div>
                  {!last && <div className="w-px flex-1 bg-border/60 my-1" />}
                </div>
                <button onClick={() => setSel(ev)} data-testid={`day-story-event-open-${i}`}
                  className={`min-w-0 flex-1 text-left rounded-lg -mx-1.5 px-1.5 py-0.5 hover:bg-secondary/50 transition-colors cursor-pointer ${last ? "" : "pb-4"}`}>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs num text-muted-foreground">{ev.time}{ev.end_time ? `–${ev.end_time}` : ""}</span>
                    <span className="text-sm font-semibold">{ev.title}</span>
                  </div>
                  <EventDetails ev={ev} />
                </button>
              </div>
            );
          })}
        </div>
        {data.habits_done?.length > 0 && (
          <div className="mt-4 pt-3 border-t border-border/40" data-testid="day-story-habits">
            <div className="text-xs text-muted-foreground mb-2">
              Привычки за день · <span className="text-foreground num font-semibold">{data.habits_done.length}</span>{data.habits_total ? <span> из <span className="num">{data.habits_total}</span></span> : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {data.habits_done.map((name, i) => (
                <span key={i} className="px-2.5 py-1 rounded-md bg-[#00e676]/10 border border-[#00e676]/25 text-xs flex items-center gap-1.5">
                  <CheckCircle size={13} weight="fill" className="text-[#00e676]" /> {name}
                </span>
              ))}
            </div>
          </div>
        )}
        </>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="glass-modal max-h-[85vh] overflow-y-auto" data-testid="day-event-dialog">
          {sel && (() => {
            const { icon: SIcon, color } = EVENT_STYLE[sel.type] || EVENT_STYLE.workout;
            const d = sel.details || {};
            const stats = sel.type === "workout" ? [
              ["Длительность", d.duration_min != null ? `${d.duration_min} мин` : null],
              ["Нагрузка", d.strain], ["Калории", d.kcal != null ? `${d.kcal} ккал` : null],
              ["Средний пульс", d.avg_hr], ["Макс. пульс", d.max_hr],
              ["Дистанция", d.distance_km != null ? `${d.distance_km} км` : null],
            ].filter(([, v]) => v != null) : [];
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2.5">
                    <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${color}1a`, border: `1px solid ${color}40` }}>
                      <SIcon size={18} weight="duotone" style={{ color }} />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate">{sel.title}</span>
                      <span className="block text-xs font-normal text-muted-foreground num">
                        {sel.time}{sel.end_time ? `–${sel.end_time}` : ""}{d.source ? ` · ${d.source}` : ""}
                      </span>
                    </span>
                  </DialogTitle>
                </DialogHeader>
                {stats.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="day-event-stats">
                    {stats.map(([label, value]) => (
                      <div key={label} className="rounded-lg bg-secondary/60 px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
                        <div className="text-sm font-bold num">{value}</div>
                      </div>
                    ))}
                  </div>
                )}
                {sel.type !== "workout" && <EventDetails ev={sel} />}
                {d.exercises && (
                  <div data-testid="day-event-exercises">
                    <div className="flex items-center gap-1.5 text-xs font-semibold mb-1.5">
                      <Barbell size={14} className="text-[#00c9a7]" />
                      Упражнения{d.log_title ? ` — ${d.log_title}` : ""}
                      <span className="text-muted-foreground num ml-auto">{d.exercises.filter((x) => x.done).length}/{d.exercises.length}</span>
                    </div>
                    <div className="space-y-1">
                      {d.exercises.map((it, i) => (
                        <button key={i} onClick={() => toggleEx(i)} disabled={!!member} data-testid={`day-event-exercise-${i}`}
                          className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-md hover:bg-secondary/40 transition-colors disabled:cursor-default">
                          <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${it.done ? "bg-[#00c9a7] border-[#00c9a7]" : "border-border"}`}>
                            {it.done && <CheckCircle size={12} weight="fill" className="text-white" />}
                          </span>
                          <span className={`text-xs ${it.done ? "line-through text-muted-foreground" : ""}`}>{it.name}</span>
                          {it.detail && <span className="text-[10px] text-muted-foreground num ml-auto shrink-0">{it.detail}</span>}
                        </button>
                      ))}
                    </div>
                    {d.strain != null && (
                      <p className="flex items-center gap-1.5 text-[11px] text-[#0084ff] mt-2">
                        <Lightning size={12} weight="fill" /> Метрики этой тренировки записаны с Whoop
                      </p>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
